<script lang="ts">
	import '../app.css'
	import Loader from '#components/loader.svelte'
	import Header from '#components/header.svelte'
	import Menu from '#components/menu.svelte'
	import Search from '#components/search.svelte'
	import Footer from '#components/footer.svelte'
	import { ui } from '#library/stores.ts'
	import Cookie from '#components/cookie.svelte'
	import { prefs } from '#library/stores.ts'
	import { browser } from '$app/env'
	import { page } from '$app/state'
	import Analytics from '#components/analytics.svelte'
	import { PUBLIC_GA_ID, PUBLIC_FB_ID } from '$app/env/public'
	import Schema from '#components/schema.svelte'
	import Meta from '#components/meta.svelte'
	import { dev } from '$app/env'
	import { inject, pageview } from '@vercel/analytics'
	import { onMount } from 'svelte'
	import { goto, onNavigate } from '$app/navigation'
	import { scrollto } from '#library/scrollto.ts'
	import Shouter, { seen } from '#components/shouter.svelte'
	import { same_page } from '#library/samepage.ts'
	import { EditBar } from 'storyloco/editor'

	onMount(() => {
		function handler(event: MouseEvent) {
			if (!(event.target instanceof HTMLElement)) return
			const anchor = event.target.closest('a')
			if (!(anchor instanceof HTMLAnchorElement)) return
			const href = anchor.getAttribute('href')
			if (!href || !href.startsWith('#')) return

			event.preventDefault()
			event.stopPropagation()

			// Smooth scroll to hashes whenever page.url.hash changes - see below
			goto(href, { replace: true, reset: false })
		}

		document.addEventListener('click', handler, true)
		return () => document.removeEventListener('click', handler, true)
	})

	// Smooth scroll to hashes whenever page.url.hash changes
	$effect(() => {
		scrollto(page.url.hash)
	})

	// ponytail: `injectAnalytics` from `@vercel/analytics/sveltekit` does this, but reads `$app/stores`,
	// which SvelteKit 3 removed; go back to it once a release reads `$app/state`
	if (browser)
		inject({
			mode: dev ? 'development' : 'production',
			debug: false,
			disableAutoTrack: true,
			framework: 'sveltekit'
		})
	$effect(() => {
		if (page.route.id) pageview({ route: page.route.id, path: page.url.pathname })
	})

	let { children, data } = $props()
	const pattern_url = $derived(`${data.layout.pattern?.filename}/m/1920x0/filters:quality(85)`)
	const pattern = $derived(`url('${pattern_url}')`)
	const illustration = $derived(`url('${data.layout.illustration?.filename}/m/1920x0/filters:quality(85)')`)
	const nano = $derived(['enquire', 'prospectus'].includes(page.data.story?.content.component ?? ''))

	const shouter = $derived.by(() => {
		const href = data.layout.callout_link?.url
		if (same_page(href, page.url)) return false

		return !browser || !seen()
	})

	if (page.url.pathname === '/') {
		$ui.immersive = true
	}

	onNavigate(navigation => {
		// Back/forward button pressed
		if (navigation.type === 'popstate') {
			$ui.menu = false
			$ui.search = false
		}
		// This works now but animation plays twice
		if (!document.startViewTransition) return

		if (navigation.type !== 'link') return
		return new Promise(resolve => {
			document.startViewTransition(async () => {
				resolve()
				await navigation.complete
			})
		})
	})
</script>

<svelte:head>
	<meta name="version" content={import.meta.env.version} />
	<meta name="build" content={import.meta.env.build} />

	<link rel="preload" as="image" href={pattern_url} fetchpriority="high" />
</svelte:head>

<Meta />
<Schema />
{#if shouter}
	<Shouter {pattern} />
{/if}

<div class="page">
	<Loader />
	<Header {nano} />

	{#if $ui.menu && !nano}
		<Menu {pattern} />
	{/if}

	<Search />

	<main
		id="top"
		class="main"
		bind:this={$ui.main}
		style:--pattern={pattern}
		style:--illustration={illustration}
		class:search={$ui.search}
	>
		{#if page.url.pathname}
			{@render children?.()}
		{/if}
	</main>

	{#if !nano}
		<Footer {pattern} />
	{/if}

	{#if browser && $prefs?.cookie === undefined}
		<Cookie />
	{/if}

	<Analytics ga_id={PUBLIC_GA_ID} fb_id={PUBLIC_FB_ID} />

	<EditBar accent="var(--c-crocus)" accent_ink="var(--c-white)" />
</div>

<style lang="css">
	.page {
		transition:
			opacity 0.5s ease,
			scale 0.5s ease;

		@starting-style {
			opacity: 0;
			scale: 0.99;
		}
	}

	.main {
		background-color: var(--c-gimson-light);
		padding-block-start: 3.75rem;
		width: 100%;
		z-index: 2;
		transition: scale 500ms ease;

		@media (--from-mobile) {
			padding-block-start: 6.9375rem;
		}

		&.search {
			scale: 0.97;
		}
	}

	@keyframes fade-in {
		from {
			opacity: 0;
			translate: 10px 0;
			scale: 0.99;
		}
	}

	@keyframes fade-out {
		to {
			opacity: 0;
			translate: -10px 0;
			scale: 0.99;
		}
	}

	:root::view-transition-old(root) {
		animation: 200ms cubic-bezier(0.4, 0, 1, 1) both fade-out;
	}

	:root::view-transition-new(root) {
		animation: 200ms cubic-bezier(0, 0, 0.2, 1) 250ms both fade-in;
	}
</style>
